import { ReactNode, useContext, useEffect, useMemo } from 'react';
import { UserContext, normalizeUser } from '../../context/UserContext';
import { useInitializeAnalyticsClient } from 'hooks/useInitializeAnalyticsClient';
import { useSentryInitialization } from 'hooks/useSentryInitialization';
import StatsigInitializer from '../../services/statsig/StatsigInitializer';
import { Cookie } from 'utils/cookie';
import Loader from 'components/common/Loader';
import { AppLoadingContext } from '../../context/AppLoadingContext';


type Props = {
  children: ReactNode;
  isCSR?: boolean;
};

export const ServicesInitializer = ({ children, isCSR }: Props) => {
  const data = useContext(UserContext);
  const { loading, setLoading } = useContext(AppLoadingContext);


  const user = data?.user;
  const userIsInitialized = data?.userIsInitialized;

  const normalizedUser = normalizeUser(user);
  const atlassianAccountId = normalizedUser.id;

  useSentryInitialization({ loggedInUser: normalizedUser, pathname: window.location.pathname });
  useInitializeAnalyticsClient({ userId: atlassianAccountId });

  const anonymousId = Cookie.readCookie('ajs_anonymous_id') ?? '';

  const identifiers = useMemo(() => {
    return { atlassianAccountId, analyticsAnonymousId: anonymousId };
  }, [atlassianAccountId, anonymousId]);

  useEffect(() => {
    if(loading.servicesInitializer === userIsInitialized) {
      setLoading((prev) => ({
        initValidator: prev.initValidator,
        servicesInitializer: !userIsInitialized
      }));
    }
  }, [userIsInitialized, setLoading]);


  // For SSR, return children directly without initialization
  if (!isCSR) {
    return <>{children}</>;
  }

  // For CSR, show loader if not initialized
  if (!userIsInitialized) {
    return <Loader />;
  }

  // User is initialized and it's CSR, wrap with StatsigInitializer
  return <StatsigInitializer identifiers={identifiers}>{children}</StatsigInitializer>;
};
